/**
 * Beneficios (spec 013, item 2; Vitor, 16/09): o cadastro em Cadastros, Beneficios (nome, se e gratis ou quanto custa,
 * se e permanente ou temporario) e a escolha de quais cada checkout oferece como order bump. Preco, duracao e efeito sao
 * do servico de pagamentos; quem cumpre a promessa e o backend.
 */
import { Aviso, Botao, CabecalhoDePagina, Campo, CampoCaixa, CampoDinheiro, CampoTextoLongo, Carregando, Pilha, Segmentado, Selo, Tabela, Vazio, formatarReais } from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { erroDe, useApi, usePapel } from "../api";
import { config } from "../config";
import { DialogoDeAcao } from "./DialogoDeAcao";
import { usePagina } from "../pagina";

const PAG = (caminho: string) => `${config().PAGAMENTOS_URL.replace(/\/$/, "")}${caminho}`;

export type Efeito = "ENTREGA" | "CONDICAO" | "PLANO_EM_DOBRO";
export type BeneficioDoCatalogo = { id: string; nome: string; descricao: string | null; preco_centavos: number; efeito: Efeito; duracao_dias: number | null; ativo: boolean };

export const EFEITO: Record<Efeito, { rotulo: string; explica: string }> = {
  ENTREGA: { rotulo: "Entrega", explica: "A equipe entrega (reset, day off). Na venda, vira pendência até alguém marcar como honrado." },
  CONDICAO: { rotulo: "Condição", explica: "Vale lá na frente (100% de repasse, margem turbinada). Fica em vigor no plano, sem pendência." },
  PLANO_EM_DOBRO: { rotulo: "Plano em dobro", explica: "Compre 1 leve 2: a venda cria um segundo plano igual, com subconta própria e o mesmo caminho." },
};

/** "Grátis" ou o preco; "Permanente" ou "30 dias". */
export const valorDoBeneficio = (b: Pick<BeneficioDoCatalogo, "preco_centavos">) => (b.preco_centavos ? formatarReais(b.preco_centavos) : "Grátis");
export const duracaoDoBeneficio = (b: Pick<BeneficioDoCatalogo, "duracao_dias">) => (b.duracao_dias ? `${b.duracao_dias} ${b.duracao_dias === 1 ? "dia" : "dias"}` : "Permanente");

export function useCatalogoDeBeneficios() {
  const { ler } = useApi();
  return useQuery({ queryKey: ["beneficios-do-catalogo"], queryFn: () => ler<{ itens: BeneficioDoCatalogo[] }>(PAG("/v1/admin/beneficios")) });
}

/**
 * Marcar os beneficios que o checkout promete. A ordem da pagina e a ordem em que foram marcados. `jaOferecidos` e o
 * que o checkout ja tinha: o desativado entre eles aparece com o aviso (antes ficava escondido e marcado, e salvar dava
 * erro; Vitor, 19/10), pra dar pra ver e tirar. Desativado que o checkout nao tinha nao aparece.
 */
export function EscolhaDeBeneficios({ escolhidos, aoMudar, jaOferecidos = [] }: { escolhidos: string[]; aoMudar: (ids: string[]) => void; jaOferecidos?: string[] }) {
  const q = useCatalogoDeBeneficios();
  const visiveis = (q.data?.itens ?? []).filter((b) => b.ativo || jaOferecidos.includes(b.id));
  if (q.isLoading) return <Carregando compacto />;
  if (!visiveis.length) return <span className="mtc-campo__dica">Sem benefícios cadastrados. Crie em Cadastros, Benefícios.</span>;
  return (
    <Pilha espaco={2}>
      <span className="mtc-rotulo">Benefícios que a oferta promete</span>
      {visiveis.map((b) => (
        <CampoCaixa key={b.id} marcado={escolhidos.includes(b.id)} disabled={!escolhidos.includes(b.id) && escolhidos.length >= 8} aoMudar={(v) => aoMudar(v ? [...escolhidos, b.id] : escolhidos.filter((x) => x !== b.id))}>
          {b.nome} <span className="mtc-texto-2">({b.ativo ? `${valorDoBeneficio(b)}, ${duracaoDoBeneficio(b).toLowerCase()}` : "desativado: fica fora da compra até ser reativado"})</span>
        </CampoCaixa>
      ))}
      <span className="mtc-campo__dica">O comprador marca os que quer. Benefício pago soma no total.</span>
    </Pilha>
  );
}

type Formulario = { nome: string; descricao: string; pago: boolean; preco: number; temporario: boolean; dias: string; efeito: Efeito };
const vazio: Formulario = { nome: "", descricao: "", pago: false, preco: 0, temporario: false, dias: "30", efeito: "ENTREGA" };
const doBeneficio = (b: BeneficioDoCatalogo): Formulario => ({ nome: b.nome, descricao: b.descricao ?? "", pago: b.preco_centavos > 0, preco: b.preco_centavos, temporario: Boolean(b.duracao_dias), dias: String(b.duracao_dias ?? 30), efeito: b.efeito });
const diasValidos = (f: Formulario) => /^\d{1,4}$/.test(f.dias) && Number(f.dias) >= 1 && Number(f.dias) <= 3650;
const pronto = (f: Formulario) => f.nome.trim().length >= 2 && (!f.pago || f.preco > 0) && (!f.temporario || f.efeito === "PLANO_EM_DOBRO" || diasValidos(f));
const corpo = (f: Formulario) => ({ nome: f.nome.trim(), descricao: f.descricao.trim() || null, preco_centavos: f.pago ? f.preco : 0, duracao_dias: f.temporario && f.efeito !== "PLANO_EM_DOBRO" ? Number(f.dias) : null });

/** Os campos do cadastro, na ordem que o Vitor pediu: nome, valor, duracao; o tipo diz o que a equipe faz com ele. */
function CamposDoBeneficio({ f, setF, novo }: { f: Formulario; setF: (f: Formulario) => void; novo: boolean }) {
  return (
    <>
      <Campo rotulo="Nome" valor={f.nome} aoMudar={(nome) => setF({ ...f, nome })} placeholder="Ex.: 1 Reset gratuito" />
      <CampoTextoLongo rotulo="Descrição (opcional)" valor={f.descricao} aoMudar={(descricao) => setF({ ...f, descricao })} maxLength={300} dica="O comprador lê no checkout." />
      <Segmentado rotulo="Valor" nome={`valor-${novo ? "novo" : "editar"}`} valor={f.pago ? "PAGO" : "GRATIS"} aoMudar={(v) => setF({ ...f, pago: v === "PAGO" })} opcoes={[{ valor: "GRATIS", rotulo: "Grátis" }, { valor: "PAGO", rotulo: "Pago" }]} />
      {f.pago && <CampoDinheiro rotulo="Quanto custa" centavos={f.preco} aoMudar={(preco) => setF({ ...f, preco })} dica="Soma no total da compra e nas parcelas." />}
      {f.efeito !== "PLANO_EM_DOBRO" && (
        <>
          <Segmentado rotulo="Duração" nome={`duracao-${novo ? "novo" : "editar"}`} valor={f.temporario ? "TEMPORARIO" : "PERMANENTE"} aoMudar={(v) => setF({ ...f, temporario: v === "TEMPORARIO" })} opcoes={[{ valor: "PERMANENTE", rotulo: "Permanente" }, { valor: "TEMPORARIO", rotulo: "Temporário" }]} />
          {f.temporario && <Campo rotulo="Vale por quantos dias" valor={f.dias} aoMudar={(dias) => setF({ ...f, dias: dias.replace(/\D/g, "").slice(0, 4) })} inputMode="numeric" erro={diasValidos(f) ? undefined : "De 1 a 3650 dias."} dica="Conta a partir de quando o plano é liberado." />}
        </>
      )}
      {novo ? (
        <>
          <Segmentado rotulo="Tipo" nome="efeito-novo-beneficio" valor={f.efeito} aoMudar={(efeito) => setF({ ...f, efeito })} opcoes={(Object.keys(EFEITO) as Efeito[]).map((e) => ({ valor: e, rotulo: EFEITO[e].rotulo }))} />
          <span className="mtc-campo__dica">{EFEITO[f.efeito].explica} O tipo não muda depois.</span>
        </>
      ) : (
        <span className="mtc-campo__dica">Tipo: {EFEITO[f.efeito].rotulo}. {EFEITO[f.efeito].explica}</span>
      )}
    </>
  );
}

export function PaginaBeneficios() {
  const pag = usePagina("pagina");
  const { postar, por } = useApi();
  const { pode } = usePapel();
  const qc = useQueryClient();
  const admin = pode("ADMIN");
  const q = useCatalogoDeBeneficios();
  const [novo, setNovo] = useState(false);
  const [editar, setEditar] = useState<BeneficioDoCatalogo | null>(null);
  const [alternar, setAlternar] = useState<BeneficioDoCatalogo | null>(null);
  const [f, setF] = useState<Formulario>(vazio);
  const [feito, setFeito] = useState<string>();
  const atualizar = () => void qc.invalidateQueries({ queryKey: ["beneficios-do-catalogo"] });
  const itens = [...(q.data?.itens ?? [])].sort((a, b) => Number(b.ativo) - Number(a.ativo) || a.nome.localeCompare(b.nome, "pt-BR"));
  return (
    <Pilha espaco={5}>
      <CabecalhoDePagina titulo="Benefícios" descricao="O que os checkouts oferecem junto com o plano, como order bump. O comprador marca os que quer; os pagos somam no total e a promessa vai pra ficha da inscrição." acoes={admin ? <Botao onClick={() => { setFeito(undefined); setF(vazio); setNovo(true); }}>Novo benefício</Botao> : undefined} />
      {feito && <Aviso tipo="sucesso">{feito}</Aviso>}
      {q.isLoading ? <Carregando compacto texto="Buscando os benefícios" /> : q.isError ? <Aviso tipo="erro">{erroDe(q.error)}</Aviso> : (
        <Tabela
          paginacao={pag}
          linhas={itens}
          chave={(b) => b.id}
          cartao="media"
          vazio={<Vazio titulo="Nenhum benefício">Crie o primeiro: o nome, se é grátis ou pago e se vale pra sempre ou por alguns dias.</Vazio>}
          colunas={[
            { chave: "nome", rotulo: "Benefício", celula: (b) => <><strong>{b.nome}</strong>{b.descricao && <span className="mtc-tabela__apoio">{b.descricao}</span>}</> },
            { chave: "valor", rotulo: "Valor", alinhar: "direita", celula: (b) => (b.preco_centavos ? <span className="mtc-mono">{formatarReais(b.preco_centavos)}</span> : <span>Grátis</span>) },
            { chave: "duracao", rotulo: "Duração", celula: (b) => (b.efeito === "PLANO_EM_DOBRO" ? <span className="mtc-texto-2">Não se aplica</span> : <>{duracaoDoBeneficio(b)}{b.duracao_dias && <span className="mtc-tabela__apoio">a partir da liberação</span>}</>) },
            { chave: "tipo", rotulo: "Tipo", celula: (b) => EFEITO[b.efeito].rotulo },
            { chave: "status", rotulo: "Status", celula: (b) => <Selo tom={b.ativo ? "sucesso" : "neutro"}>{b.ativo ? "Ativo" : "Desativado"}</Selo> },
            ...(admin ? [{ chave: "acoes", rotulo: "Ações", rotuloOculto: true, celula: (b: BeneficioDoCatalogo) => (
              <div className="mtc-linha" style={{ gap: "var(--e-2)", flexWrap: "nowrap" }}>
                <Botao tamanho="pequeno" variante="fantasma" aria-label={`Editar ${b.nome}`} onClick={() => { setFeito(undefined); setF(doBeneficio(b)); setEditar(b); }}>Editar</Botao>
                <Botao tamanho="pequeno" variante="fantasma" aria-label={`${b.ativo ? "Desativar" : "Ativar"} ${b.nome}`} onClick={() => { setFeito(undefined); setAlternar(b); }}>{b.ativo ? "Desativar" : "Ativar"}</Botao>
              </div>
            ) }] : []),
          ]}
        />
      )}
      <DialogoDeAcao aberto={novo} aoFechar={() => setNovo(false)} titulo="Novo benefício" botao="Criar benefício" podeEnviar={pronto(f)} aoEnviar={() => postar(PAG("/v1/admin/beneficios"), { ...corpo(f), efeito: f.efeito })} aoConcluir={() => { setNovo(false); atualizar(); setFeito(`Benefício ${f.nome.trim()} criado. Ofereça nos checkouts pelo botão Benefícios de cada um.`); }}>
        <CamposDoBeneficio f={f} setF={setF} novo />
      </DialogoDeAcao>
      <DialogoDeAcao aberto={Boolean(editar)} variante="primario" aoFechar={() => setEditar(null)} titulo="Editar benefício" descricao="Vale pras próximas compras. Quem já comprou fica com o que comprou." botao="Salvar" podeEnviar={pronto(f)} aoEnviar={() => por(PAG(`/v1/admin/beneficios/${editar!.id}`), corpo(f))} aoConcluir={() => { setEditar(null); atualizar(); setFeito(`Benefício ${f.nome.trim()} salvo.`); }}>
        <CamposDoBeneficio f={f} setF={setF} novo={false} />
      </DialogoDeAcao>
      <DialogoDeAcao
        aberto={Boolean(alternar)}
        aoFechar={() => setAlternar(null)}
        titulo={alternar ? `${alternar.ativo ? "Desativar" : "Ativar"} o benefício ${alternar.nome}?` : ""}
        descricao={alternar ? (alternar.ativo ? "Ele sai da oferta de todos os checkouts. Quem já comprou continua com ele." : "Ele volta a aparecer nos checkouts que já ofereciam.") : undefined}
        botao={alternar?.ativo ? "Desativar benefício" : "Ativar benefício"}
        variante={alternar?.ativo ? "perigo" : "primario"}
        aoEnviar={() => por(PAG(`/v1/admin/beneficios/${alternar!.id}`), { ativo: !alternar!.ativo })}
        aoConcluir={() => { const b = alternar!; setAlternar(null); atualizar(); setFeito(`Benefício ${b.nome} ${b.ativo ? "desativado" : "ativado"}.`); }}
      />
    </Pilha>
  );
}

/** Trocar os beneficios de um checkout que ja existe (preco e plano ficam). */
export function DialogoBeneficiosDoCheckout({ checkout, aoFechar, aoConcluir }: { checkout: { id: string; nome: string; beneficios: { id: string }[] } | null; aoFechar: () => void; aoConcluir: () => void }) {
  const { por } = useApi();
  // Sem mexer, vale o que o checkout ja tem, lido ao desenhar. Antes vinha de um efeito depois de abrir: um clique logo
  // na abertura partia da lista vazia e salvar apagava os beneficios do checkout (visto no e2e, 19/10).
  const [mexidos, setMexidos] = useState<string[] | null>(null);
  const ids = mexidos ?? checkout?.beneficios.map((b) => b.id) ?? [];
  const fechar = () => { setMexidos(null); aoFechar(); };
  return (
    <DialogoDeAcao aberto={Boolean(checkout)} aoFechar={fechar} titulo="Benefícios do checkout" descricao={checkout ? `O que ${checkout.nome} promete. Vale pras próximas compras; preço e plano não mudam.` : undefined} botao="Salvar" aoEnviar={() => por(PAG(`/v1/admin/checkouts/${checkout!.id}/beneficios`), { beneficio_ids: ids })} aoConcluir={aoConcluir}>
      <EscolhaDeBeneficios escolhidos={ids} aoMudar={setMexidos} jaOferecidos={checkout?.beneficios.map((b) => b.id)} />
    </DialogoDeAcao>
  );
}
